import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, 
  Users, 
  Briefcase, 
  Award, 
  AlertTriangle, 
  Clock, 
  Target, 
  BarChart3,
  Plus,
  Eye,
  Edit,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import {
  allocateAllProjects,
  analyzeTeamCapacity,
  assessProjectRisk,
  calculatePersonPerformance,
  getRecommendations,
  getCertificationStatus
} from '../utils/skillMatching';

const EnhancedDashboard = () => {
  const { isDarkMode } = useTheme();
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // overview, people, projects, analytics

  // Calculate advanced metrics using useMemo for performance
  const metrics = useMemo(() => {
    const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
    const projects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
    const skills = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
    
    const totalPeople = people.length;
    const totalProjects = projects.length;
    const totalSkills = skills.length;
    
    const activeProjects = projects.filter(p => p.status === 'In Progress').length;
    const planningProjects = projects.filter(p => p.status === 'Planning').length;
    
    const totalHoursRequired = projects.reduce((sum, p) => 
      sum + (p.requiredSkills?.reduce((hSum, skill) => hSum + (skill.hours || 0), 0) || 0), 0
    );
    
    const expiringCertifications = people.reduce((sum, p) => 
      sum + (p.certs?.filter(c => getCertificationStatus(c) === 'Expiring Soon').length || 0), 0
    );
    
    const criticalSkillGaps = skills.filter(s => s.gap > 0).length;
    
    return {
      totalPeople,
      totalProjects,
      totalSkills,
      activeProjects,
      planningProjects,
      totalHoursRequired,
      expiringCertifications,
      criticalSkillGaps
    };
  }, []);

  // Calculate skill gaps analysis
  const skillGaps = useMemo(() => {
    try {
      const skills = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
      return skills.filter(skill => skill.gap > 0).map(skill => ({
        skill: skill.name,
        gap: skill.gap,
        importance: skill.level,
        requiredHours: skill.projectDemand,
        availableHours: skill.peopleCount,
        project: skill.category
      }));
    } catch (error) {
      console.error('Error calculating skill gaps:', error);
      return [];
    }
  }, []);

  // Calculate team capacity
  const teamCapacity = useMemo(() => {
    try {
      const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      return analyzeTeamCapacity({ people });
    } catch (error) {
      console.error('Error calculating team capacity:', error);
      return {};
    }
  }, []);

  // Calculate project allocation
  const projectAllocation = useMemo(() => {
    try {
      const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const projects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      return allocateAllProjects({ people, projects });
    } catch (error) {
      console.error('Error calculating project allocation:', error);
      return { rows: [], perProject: new Map() };
    }
  }, []);

  // Calculate top matches for selected project
  const topMatches = useMemo(() => {
    if (!selectedProject) return [];
    
    const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
    const matches = people.map(person => ({
      person,
      scores: {
        skill: Math.random() * 0.4 + 0.6, // Mock skill score
        availability: person.availability || 0.5,
        total: Math.random() * 0.3 + 0.7 // Mock total score
      }
    })).sort((a, b) => b.scores.total - a.scores.total);
    
    return matches.slice(0, 5);
  }, [selectedProject]);

  // Calculate person performance
  const personPerformance = useMemo(() => {
    if (!selectedPerson) return null;
    const projects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
    return calculatePersonPerformance(selectedPerson, projects);
  }, [selectedPerson]);

  // Calculate recommendations
  const recommendations = useMemo(() => {
    if (!selectedPerson) return [];
    const projects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
    const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
    return getRecommendations(selectedPerson, projects, people);
  }, [selectedPerson]);

  // Calculate project risks
  const projectRisks = useMemo(() => {
    if (!selectedProject) return [];
    const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
    return assessProjectRisk(selectedProject, people);
  }, [selectedProject]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-500';
      case 'In Progress': return 'text-blue-500';
      case 'Planning': return 'text-yellow-500';
      case 'On Hold': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-500 bg-red-100 dark:bg-red-900/20';
      case 'High': return 'text-orange-500 bg-orange-100 dark:bg-orange-900/20';
      case 'Medium': return 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/20';
      case 'Low': return 'text-green-500 bg-green-100 dark:bg-green-900/20';
      default: return 'text-gray-500 bg-gray-100 dark:bg-gray-900/20';
    }
  };



  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-dark-950 text-white' : 'bg-light-50 text-light-950'}`}>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 gradient-text">
            Enhanced Dashboard
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
            Advanced analytics, real-time allocation, and intelligent insights
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-8 bg-gray-100 dark:bg-dark-800 rounded-lg p-1">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'people', label: 'People Analytics', icon: Users },
            { id: 'projects', label: 'Project Management', icon: Briefcase },
            { id: 'analytics', label: 'Advanced Analytics', icon: TrendingUp }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  viewMode === tab.id
                    ? 'bg-white dark:bg-dark-700 text-primary-600 shadow-sm'
                    : 'text-gray-600 dark:text-dark-400 hover:text-gray-800 dark:hover:text-dark-200'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Overview Mode */}
        {viewMode === 'overview' && (
          <div className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Total People', value: metrics.totalPeople, icon: Users, color: 'bg-blue-500' },
                { label: 'Active Projects', value: metrics.activeProjects, icon: Briefcase, color: 'bg-green-500' },
                { label: 'Total Skills', value: metrics.totalSkills, icon: Award, color: 'bg-purple-500' },
                { label: 'Hours Required', value: metrics.totalHoursRequired, icon: Clock, color: 'bg-orange-500' }
              ].map((metric) => (
                <div key={metric.label} className={`card ${isDarkMode ? 'dark' : 'light'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-dark-400' : 'text-light-600'}`}>
                        {metric.label}
                      </p>
                      <p className="text-2xl font-bold">{metric.value}</p>
                    </div>
                    <div className={`p-3 rounded-full ${metric.color} text-white`}>
                      <metric.icon size={24} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Critical Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <AlertTriangle className="text-red-500 mr-2" size={20} />
                  Critical Alerts
                </h3>
                <div className="space-y-3">
                  {metrics.expiringCertifications > 0 && (
                    <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <span className="text-red-700 dark:text-red-300">
                        {metrics.expiringCertifications} certifications expiring soon
                      </span>
                      <AlertTriangle className="text-red-500" size={16} />
                    </div>
                  )}
                  {metrics.criticalSkillGaps > 0 && (
                    <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <span className="text-orange-700 dark:text-orange-300">
                        {metrics.criticalSkillGaps} critical skill gaps identified
                      </span>
                      <Target className="text-orange-500" size={16} />
                    </div>
                  )}
                  {metrics.expiringCertifications === 0 && metrics.criticalSkillGaps === 0 && (
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <span className="text-green-700 dark:text-green-300">
                        All systems operational
                      </span>
                      <CheckCircle className="text-green-500" size={16} />
                    </div>
                  )}
                </div>
              </div>

              <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <TrendingUp className="text-blue-500 mr-2" size={20} />
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-center space-x-2 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">
                    <Plus size={16} />
                    <span>Create New Project</span>
                  </button>
                  <button className="w-full flex items-center justify-center space-x-2 p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
                    <Users size={16} />
                    <span>Add Team Member</span>
                  </button>
                  <button 
                    onClick={() => window.location.href = '/skills/new'}
                    className="w-full flex items-center justify-center space-x-2 p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  >
                    <Award size={16} />
                    <span>Add New Skill</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Skill Gap Analysis */}
            <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
              <h3 className="text-lg font-semibold mb-4">Critical Skill Gaps</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-dark-700' : 'border-light-200'}`}>
                      <th className="text-left py-3 px-4">Skill</th>
                      <th className="text-left py-3 px-4">Gap %</th>
                      <th className="text-left py-3 px-4">Demand Hours</th>
                      <th className="text-left py-3 px-4">Supply Hours</th>
                      <th className="text-left py-3 px-4">Critical Projects</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skillGaps.slice(0, 5).map((gap, index) => (
                      <tr key={index} className={`border-b ${isDarkMode ? 'border-dark-800' : 'border-light-100'}`}>
                        <td className="py-3 px-4 font-medium">{gap.skill}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            gap.importance === 'Critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' :
                            gap.importance === 'High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300' :
                            gap.importance === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300' :
                            'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                          }`}>
                            {gap.gap > 0 ? 'High' : 'Low'}
                          </span>
                        </td>
                        <td className="py-3 px-4">{gap.requiredHours || 0}h</td>
                        <td className="py-3 px-4">{gap.availableHours || 0}h</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 rounded text-xs">
                              {gap.project}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Team Capacity Overview */}
            <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
              <h3 className="text-lg font-semibold mb-4">Team Capacity Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(teamCapacity).map(([teamName, team]) => (
                  <div key={teamName} className={`p-4 rounded-lg border ${
                    isDarkMode ? 'border-dark-700 bg-dark-800' : 'border-light-200 bg-white'
                  }`}>
                    <h4 className="font-medium mb-2">{teamName}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Members:</span>
                        <span>{team.totalPeople}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Utilization:</span>
                        <span className={`font-medium ${
                          team.avgAvailability > 0.8 ? 'text-red-500' :
                          team.avgAvailability > 0.6 ? 'text-orange-500' :
                          team.avgAvailability > 0.4 ? 'text-yellow-500' :
                          'text-green-500'
                        }`}>
                          {Math.round(team.avgAvailability * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            team.avgAvailability > 0.8 ? 'bg-red-500' :
                            team.avgAvailability > 0.6 ? 'bg-orange-500' :
                            team.avgAvailability > 0.4 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(team.avgAvailability * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* People Analytics Mode */}
        {viewMode === 'people' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">People Analytics</h2>
              <div className="flex space-x-3">
                <button 
                  onClick={() => window.location.href = '/skills/new'}
                  className="btn-secondary"
                >
                  <Award size={16} className="mr-2" />
                  Add Skill
                </button>
                <button 
                  onClick={() => window.location.href = '/people/new'}
                  className="btn-primary"
                >
                  <Plus size={16} className="mr-2" />
                  Add Person
                </button>
              </div>
            </div>

            {/* People Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {JSON.parse(localStorage.getItem('skillmesh_people') || '[]').map((person) => (
                <div 
                  key={person.id} 
                  className={`card ${isDarkMode ? 'dark' : 'light'} cursor-pointer transition-all duration-200 hover:shadow-lg ${
                    selectedPerson?.id === person.id ? 'ring-2 ring-primary-500' : ''
                  }`}
                  onClick={() => setSelectedPerson(person)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">{person.name}</h3>
                      <p className={`text-sm ${isDarkMode ? 'text-dark-400' : 'text-light-600'}`}>
                        {person.role} • {person.team}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded">
                        <Eye size={16} />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded">
                        <Edit size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Availability:</span>
                      <span className="font-medium">{Math.round((person.availability || 0) * 100)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Hours Available:</span>
                      <span className="font-medium">{person.hoursAvail || 0}h</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Skills:</span>
                      <span className="font-medium">{person.skills?.length || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Certifications:</span>
                      <span className="font-medium">{person.certs?.length || 0}</span>
                    </div>
                  </div>

                  {/* Skills Preview */}
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-1">
                      {person.skills?.slice(0, 3).map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded text-xs">
                          {typeof skill === 'string' ? skill : skill.skill} {typeof skill === 'object' ? `L${skill.level}` : ''}
                        </span>
                      ))}
                      {person.skills && person.skills.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs">
                          +{person.skills.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Person Details */}
            {selectedPerson && (
              <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold">{selectedPerson.name}</h3>
                    <p className={`text-lg ${isDarkMode ? 'text-dark-400' : 'text-light-600'}`}>
                      {selectedPerson.role} • {selectedPerson.team}
                    </p>
                  </div>
                  <button 
                    onClick={() => setSelectedPerson(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-full"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Performance Metrics */}
                  <div>
                    <h4 className="font-semibold mb-3">Performance Metrics</h4>
                    {personPerformance && (
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span>Assigned Projects:</span>
                          <span className="font-medium">{personPerformance.assignedProjects}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Hours:</span>
                          <span className="font-medium">{personPerformance.totalHours}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Completion Rate:</span>
                          <span className="font-medium">{personPerformance.completionRate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Skill Utilization:</span>
                          <span className="font-medium">{personPerformance.skillUtilization}/5</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Overall Performance:</span>
                          <span className="font-medium">{personPerformance.performance}%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h4 className="font-semibold mb-3">Recommendations</h4>
                    <div className="space-y-3">
                      {recommendations.map((rec, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                          rec.priority === 'High' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                          rec.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
                          'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{rec.type}</p>
                              <p className="text-sm mt-1">{rec.description}</p>
                              <p className="text-xs mt-1 opacity-75">{rec.impact}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              rec.priority === 'High' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                              rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' :
                              'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                            }`}>
                              {rec.priority}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Project Management Mode */}
        {viewMode === 'projects' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Project Management</h2>
              <div className="flex space-x-3">
                <button 
                  onClick={() => window.location.href = '/skills/new'}
                  className="btn-secondary"
                >
                  <Award size={16} className="mr-2" />
                  Add Skill
                </button>
                <button 
                  onClick={() => window.location.href = '/projects/new'}
                  className="btn-primary"
                >
                  <Plus size={16} className="mr-2" />
                  Create Project
                </button>
              </div>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {JSON.parse(localStorage.getItem('skillmesh_projects') || '[]').map((project) => (
                <div 
                  key={project.id} 
                  className={`card ${isDarkMode ? 'dark' : 'light'} cursor-pointer transition-all duration-200 hover:shadow-lg ${
                    selectedProject?.id === project.id ? 'ring-2 ring-primary-500' : ''
                  }`}
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-2">{project.name}</h3>
                      <p className={`text-sm ${isDarkMode ? 'text-dark-400' : 'text-light-600'} mb-2`}>
                        {project.description}
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className={`${getStatusColor(project.status)}`}>
                          {project.status}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority || 'Medium')}`}>
                          {project.priority || 'Medium'}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Eye size={16} />
                      </button>
                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <Edit size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Team Size:</span>
                      <span className="font-medium">{project.teamSize}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Deadline:</span>
                      <span className="font-medium">{project.endDate}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Required Skills:</span>
                      <span className="font-medium">{project.requiredSkills?.length || 0}</span>
                    </div>
                  </div>

                  {/* Skills Preview */}
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-1">
                      {project.requiredSkills?.slice(0, 3).map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs">
                          {typeof skill === 'string' ? skill : skill.name} {typeof skill === 'object' ? `L${skill.level}` : ''}
                        </span>
                      ))}
                      {project.requiredSkills && project.requiredSkills.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs">
                          +{project.requiredSkills.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Project Details */}
            {selectedProject && (
              <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold">{selectedProject.name}</h3>
                    <p className={`text-lg ${isDarkMode ? 'text-dark-400' : 'text-light-600'}`}>
                      {selectedProject.status}
                    </p>
                  </div>
                  <button 
                    onClick={() => setSelectedProject(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-full"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Project Details */}
                  <div>
                    <h4 className="font-semibold mb-3">Project Details</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Status:</span>
                        <span className={`font-medium ${getStatusColor(selectedProject.status)}`}>
                          {selectedProject.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Priority:</span>
                        <span className={`font-medium px-2 py-1 rounded text-xs ${getPriorityColor(selectedProject.priority || 'Medium')}`}>
                          {selectedProject.priority || 'Medium'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Deadline:</span>
                        <span className="font-medium">{selectedProject.endDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Team Size:</span>
                        <span className="font-medium">{selectedProject.teamSize}</span>
                      </div>
                    </div>
                  </div>

                  {/* Risk Assessment */}
                  <div>
                    <h4 className="font-semibold mb-3">Risk Assessment</h4>
                    <div className="space-y-3">
                      {projectRisks.map((risk, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                          risk.severity === 'High' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                          risk.severity === 'Medium' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
                          'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{risk.type}</p>
                              <p className="text-sm mt-1">{risk.description}</p>
                              <p className="text-xs mt-1 opacity-75">{risk.impact}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              risk.severity === 'High' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                              risk.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' :
                              'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                            }`}>
                              {risk.severity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Top Matches */}
                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Top Matches for This Project</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${isDarkMode ? 'border-dark-700' : 'border-light-200'}`}>
                          <th className="text-left py-3 px-4">Person</th>
                          <th className="text-left py-3 px-4">Role</th>
                          <th className="text-left py-3 px-4">Skill Score</th>
                          <th className="text-left py-3 px-4">Availability</th>
                          <th className="text-left py-3 px-4">Total Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topMatches.map((match) => (
                          <tr key={match.person.id} className={`border-b ${isDarkMode ? 'border-dark-800' : 'border-light-100'}`}>
                            <td className="py-3 px-4 font-medium">{match.person.name}</td>
                            <td className="py-3 px-4">{match.person.role}</td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs">
                                {Math.round(match.scores.skill * 100)}%
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-xs">
                                {Math.round(match.scores.availability * 100)}%
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded text-xs font-medium">
                                {Math.round(match.scores.total * 100)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Advanced Analytics Mode */}
        {viewMode === 'analytics' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold">Advanced Analytics</h2>

            {/* Project Allocation Matrix */}
            <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
              <h3 className="text-lg font-semibold mb-4">Project Allocation Matrix</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-dark-700' : 'border-light-200'}`}>
                      <th className="text-left py-3 px-4">Project</th>
                      <th className="text-left py-3 px-4">Skill</th>
                      <th className="text-left py-3 px-4">Level</th>
                      <th className="text-left py-3 px-4">Person</th>
                      <th className="text-left py-3 px-4">Hours</th>
                      <th className="text-left py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectAllocation.rows?.slice(0, 20).map((row, idx) => (
                      <tr key={idx} className={`border-b ${isDarkMode ? 'border-dark-800' : 'border-light-100'}`}>
                        <td className="py-3 px-4 font-medium">{row.projectTitle}</td>
                        <td className="py-3 px-4">{row.skill}</td>
                        <td className="py-3 px-4">L{row.level}</td>
                        <td className="py-3 px-4">
                          {row.personName || (
                            <span className="text-red-500 font-medium">UNASSIGNED</span>
                          )}
                        </td>
                        <td className="py-3 px-4">{row.hours}h</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            row.personName 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                          }`}>
                            {row.personName ? 'Assigned' : 'Unassigned'}
                          </span>
                        </td>
                      </tr>
                    )) || []}
                  </tbody>
                </table>
              </div>
              {projectAllocation.rows && projectAllocation.rows.length > 20 && (
                <p className="text-sm text-center mt-4 opacity-75">
                  Showing first 20 allocations of {projectAllocation.rows.length} total
                </p>
              )}
            </div>

            {/* Team Capacity Analysis */}
            <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
              <h3 className="text-lg font-semibold mb-4">Team Capacity Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(teamCapacity).map(([teamName, team]) => (
                  <div key={teamName} className={`p-4 rounded-lg border ${
                    isDarkMode ? 'border-dark-700 bg-dark-800' : 'border-light-200 bg-white'
                  }`}>
                    <h4 className="font-medium mb-3">{teamName}</h4>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span>Members:</span>
                        <span className="font-medium">{team.totalPeople}</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span>Total Hours:</span>
                        <span className="font-medium">{team.totalHours}h</span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Utilization:</span>
                          <span className={`font-medium ${
                            team.avgAvailability > 0.8 ? 'text-red-500' :
                            team.avgAvailability > 0.6 ? 'text-orange-500' :
                            team.avgAvailability > 0.4 ? 'text-yellow-500' :
                            'text-green-500'
                          }`}>
                            {Math.round(team.avgAvailability * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              team.avgAvailability > 0.8 ? 'bg-red-500' :
                              team.avgAvailability > 0.6 ? 'bg-orange-500' :
                              team.avgAvailability > 0.4 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(team.avgAvailability * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certification Status Overview */}
            <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
              <h3 className="text-lg font-semibold mb-4">Certification Status Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {['Valid', 'Warning', 'Expiring Soon', 'Expired'].map((status) => {
                  const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
                  const count = people.reduce((sum, person) => {
                    return sum + (person.certs?.filter(cert => getCertificationStatus(cert) === status).length || 0);
                  }, 0);
                  
                  const color = status === 'Valid' ? 'green' : 
                               status === 'Warning' ? 'yellow' : 
                               status === 'Expiring Soon' ? 'orange' : 'red';
                  
                  return (
                    <div key={status} className={`p-4 rounded-lg border ${
                      isDarkMode ? 'border-dark-700 bg-dark-800' : 'border-light-200 bg-white'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium opacity-75">{status}</p>
                          <p className="text-2xl font-bold">{count}</p>
                        </div>
                        <div className={`p-3 rounded-full bg-${color}-100 dark:bg-${color}-900/20`}>
                          <Award className={`text-${color}-500`} size={24} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedDashboard;
